import { IStepGeometry, IStringerType } from '@stair-craft/common/util';

import { RISER_BOARD_THICKNESS, RISER_KOSOUR_PROJECTION, STRINGER_INSET, STRINGER_THICKNESS } from '../../constants';
import { IPlanPoint } from '../shared-plan-geometry/plan-geometry';

type IWinderCorners = NonNullable<IStepGeometry['winderCorners']>;

/**
 * Where a riser runs along the tread width (from `position.x`): its `riserSpan` or the whole
 * width; on kosour boards a riser end that reaches a stair edge covers the board's end and runs
 * `RISER_KOSOUR_PROJECTION` past its outer face (SC-CALC-089).
 */
export function riserExtent(step: IStepGeometry, stringerType: IStringerType): [number, number] {
    const span: { start: number; end: number } = step.riserSpan ?? { start: 0, end: step.treadWidth };
    if (stringerType !== 'kosour') {
        return [span.start, span.end];
    }
    // Against a turn block there is no notch to cover: the riser stops at the board's inner face.
    const edge: number = step.riserAtTurn ? STRINGER_INSET + STRINGER_THICKNESS : STRINGER_INSET - RISER_KOSOUR_PROJECTION;
    const start: number = span.start <= 0 ? edge : span.start;
    const end: number = span.end >= step.treadWidth ? step.treadWidth - edge : span.end;
    return [start, end];
}

/**
 * Where the closing riser behind the last tread runs: the kosour's last rise has no notch, so
 * there is no board end to cover and the riser stops at the boards' inner faces.
 */
export function exitRiserExtent(step: IStepGeometry, stringerType: IStringerType): [number, number] {
    return riserExtent({ ...step, riserAtTurn: true }, stringerType);
}

/** Marks the risers that stand against a turn block: a landing's own and the first one after a landing or a winder. */
export function markTurnRisers(steps: readonly IStepGeometry[]): void {
    const isTurn: (step: IStepGeometry) => boolean = (step: IStepGeometry): boolean => step.isLanding || step.isWinder;
    steps.forEach((step: IStepGeometry, index: number) => {
        const afterTurn: boolean = index > 0 && isTurn(steps[index - 1]) && !isTurn(step);
        if (step.isLanding || afterTurn) {
            step.riserAtTurn = true;
        }
    });
}

/** Bottom and top of a riser under its own tread: from the tread below (or the floor) to this tread's bottom. */
export function riserSpanY(step: IStepGeometry): { yBot: number; yTop: number } {
    return {
        yTop: step.position.y - step.treadThickness,
        yBot: Math.max(0, step.position.y - step.riserHeight - step.treadThickness),
    };
}

/** A winder riser without a precomputed outline: the board behind the leading edge, end to end. */
export function defaultWinderRiserOutline(corners: IWinderCorners): IPlanPoint[] {
    const { innerBack, outerBack, innerFront, outerFront } = corners;
    const ex: number = outerBack.x - innerBack.x;
    const ez: number = outerBack.z - innerBack.z;
    const len: number = Math.max(1e-9, Math.hypot(ex, ez));
    let nx: number = -ez / len;
    let nz: number = ex / len;
    const midX: number = (innerFront.x + outerFront.x) / 2 - innerBack.x;
    const midZ: number = (innerFront.z + outerFront.z) / 2 - innerBack.z;
    if (nx * midX + nz * midZ < 0) {
        nx = -nx;
        nz = -nz;
    }
    const t: number = RISER_BOARD_THICKNESS;
    return [
        innerBack,
        outerBack,
        { x: outerBack.x + nx * t, z: outerBack.z + nz * t },
        { x: innerBack.x + nx * t, z: innerBack.z + nz * t },
    ];
}

/** The plan outline of a winder's riser board: its own, or the default one behind the leading edge. */
export function winderRiserBoardOutline(step: IStepGeometry, corners: IWinderCorners): IPlanPoint[] {
    return step.riserOutline ?? defaultWinderRiserOutline(corners);
}
